import {supabase} from './supabase';

export const LEGACY_SKILL_DRAFT_KEY='agentordinary-review-skill-library-v1';
export const LEGACY_SOURCE_QUEUE_KEY='agentordinary-review-skill-import-queue-v1';

export type OwnerSkillDraft={
 id:string; name:string; description:string; category:string; instructions:string;
 status:'draft'|'needs review'; assignedTo:string[];createdAt:string;
 inputs?:string[]; outputs?:string[]; tools?:string[]; safetyRules?:string[];
 presetRef?:string;recordId?:string;
};
export type OwnerSkillCandidate={
 id:string;name:string;url:string;notes:string;status:'pending review';recordId?:string;
};
export type ImportedSkillRecord={
 kind:'imported'|'profile';id:string;name:string;description:string;category:string;
 profileSteps?:string;profileSuccess?:string;
 state:string;sourceUrl:string;repository:string;license:string;
 ready:boolean;assignedRoles:string[];preparedRoles:string[];updatedAt?:string;
};
export type OwnerLibrarySnapshot={
 imported:ImportedSkillRecord[];drafts:OwnerSkillDraft[];queue:OwnerSkillCandidate[];
 importedCount:number;draftCount:number;reviewQueueCount:number;
 totalCount:number;isOwnerAuthenticated:true;
};
type UnknownRecord=Record<string,unknown>;
const obj=(v:unknown):UnknownRecord=>v&&typeof v==='object'&&!Array.isArray(v)?v as UnknownRecord:{};
const arr=(v:unknown):string[]=>Array.isArray(v)?v.filter((x):x is string=>typeof x==='string').slice(0,80):[];
const string=(v:unknown,n=5000)=>typeof v==='string'?v.trim().slice(0,n):'';
const forbidden=(v:string)=>/cot-contrarian-detector|contrarian positioning/i.test(v);
const isUuid=(v:string)=>/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);

async function rpc(name:string,params:UnknownRecord):Promise<unknown>{
 const {data,error}=await supabase.rpc(name,params);
 if(error)throw new Error(error.message||'Skill registry operation failed.');
 return data;
}

export async function requireSkillOwner(){
 const {data,error}=await supabase.auth.getUser();
 if(error||!data.user)throw new Error('Sign in to AgentOrdinary as the workspace owner to manage skills.');
 return data.user.id;
}

export async function loadUnifiedSkillLibrary():Promise<OwnerLibrarySnapshot>{
 await requireSkillOwner();
 const all:UnknownRecord[]=[];
 let first:UnknownRecord|null=null;
 for(let offset=0;offset<2000;offset+=200){
  const data=obj(await rpc('agentopia_owner_skill_library_v3',{
   p_search:'',p_limit:200,p_offset:offset}));
  if(data.version!==3||!Array.isArray(data.items))
   throw new Error('Unified skill registry returned an unexpected response.');
  if(!first)first=data;
  all.push(...data.items.map(obj));
  if(!data.hasMore)break;
  if(offset>=1800)throw new Error('Registry exceeds safe page limit. Refine or paginate the library.');
 }
 if(!first)throw new Error('Skill registry has no response.');
 const imported:ImportedSkillRecord[]=[];
 const drafts:OwnerSkillDraft[]=[];
 const queue:OwnerSkillCandidate[]=[];
 for(const item of all){
  const kind=string(item.kind,32);
  if(kind==='imported'){
   const id=string(item.id,320),name=string(item.name,220);
   if(!id||!name||forbidden(id)||forbidden(name))continue;
   imported.push({
    kind:'imported',id,name,description:string(item.description,7000),
    category:string(item.category,90),state:string(item.state,50),
    repository:string(item.repository,400),license:string(item.license,90),
    sourceUrl:string(item.sourceUrl,1300),ready:item.ready===true,
    assignedRoles:arr(item.assignedRoles),preparedRoles:arr(item.preparedRoles),updatedAt:string(item.updatedAt,100)
   });
  }else if(kind==='draft'){
   const body=obj(item.body);const id=string(body.id,110);
   if(!id||forbidden(string(body.name)))continue;
   drafts.push({
    id,recordId:string(item.id,80),name:string(body.name,80),
    description:string(body.description,240),category:string(body.category,90)||'General',
    instructions:string(body.instructions,5000),
    status:body.status==='needs review'?'needs review':'draft',
    assignedTo:arr(body.assignedTo),createdAt:string(body.createdAt,80)||string(item.updatedAt,80),
    inputs:arr(body.inputs),outputs:arr(body.outputs),tools:arr(body.tools),
    safetyRules:arr(body.safetyRules),presetRef:string(body.presetRef,220)||undefined
   });
  }else if(kind==='pending_import'){
   const body=obj(item.body);const id=string(body.id,110);
   if(!id||forbidden(string(body.name))||forbidden(string(body.url)))continue;
   queue.push({id,name:string(body.name,90),url:string(body.url,1600),
    notes:string(body.notes,400),status:'pending review',recordId:string(item.id,80)});
  }
 }
 return {
  imported,drafts,queue,importedCount:imported.length,
  draftCount:drafts.length,reviewQueueCount:queue.length,
  totalCount:imported.length+drafts.length+queue.length,isOwnerAuthenticated:true
 };
}
async function editRecord(kind:'document'|'source',action:'save'|'delete',
 label?:string,body?:UnknownRecord,recordId?:string):Promise<unknown>{
 await requireSkillOwner();
 if(action==='delete'&&(!recordId||!isUuid(recordId)))
  throw new Error('Cannot remove an unsynchronized record. Refresh the skill library.');
 return rpc('agentopia_owner_skill_record_api',{
  p_action:action,p_kind:kind,p_label:label||null,p_body:body||null,p_id:recordId||null
 });
}
export async function saveOwnerSkillDraft(draft:OwnerSkillDraft):Promise<void>{
 if(!draft.id||draft.id.length>110||!draft.name.trim()||forbidden(draft.name))
  throw new Error('A valid free skill name is required.');
 const body:UnknownRecord={
  registryKind:'owner_skill_draft_v3',id:draft.id,name:draft.name,
  description:draft.description,category:draft.category,instructions:draft.instructions,
  status:draft.status,assignedTo:draft.assignedTo,createdAt:draft.createdAt,
  inputs:draft.inputs||[],outputs:draft.outputs||[],tools:draft.tools||[],
  safetyRules:draft.safetyRules||[],presetRef:draft.presetRef||''
 };
 if(JSON.stringify(body).length>24000)throw new Error('Skill draft exceeds the database limit.');
 await editRecord('document','save','owner-skill-draft:'+draft.id,body);
}
export async function removeOwnerSkillDraft(draft:OwnerSkillDraft):Promise<void>{
 await editRecord('document','delete',undefined,undefined,draft.recordId);
}
export async function saveOwnerSkillCandidate(candidate:OwnerSkillCandidate):Promise<void>{
 if(!candidate.id||!candidate.name||forbidden(candidate.name)||forbidden(candidate.url))
  throw new Error('Invalid or excluded skill source.');
 const url=new URL(candidate.url);
 if(url.protocol!=='https:'||url.username||url.password||url.port||!url.hostname.includes('.'))
  throw new Error('Use a public HTTPS source URL without embedded credentials.');
 const body={registryKind:'owner_skill_source_v3',id:candidate.id,
  name:candidate.name,url:url.href,notes:candidate.notes,status:'pending review'};
 await editRecord('source','save','owner-skill-source:'+candidate.id,body);
}
export async function removeOwnerSkillCandidate(candidate:OwnerSkillCandidate):Promise<void>{
 await editRecord('source','delete',undefined,undefined,candidate.recordId);
}
function readOldArray(key:string):UnknownRecord[]{
 try{const raw=localStorage.getItem(key);const val=raw?JSON.parse(raw):[];
  return Array.isArray(val)?val.map(obj).slice(0,250):[];
 }catch{return [];}
}

/** Owner-confirmed signed-in transfer. Existing Supabase entries always take precedence.
 * Remove legacy keys only after every remote write succeeds. Imported scripts never execute.
 */
export async function migratePreviousDeviceSkills():Promise<{drafts:number;sources:number}>{
 await requireSkillOwner();
 const oldDrafts=readOldArray(LEGACY_SKILL_DRAFT_KEY);
 const oldQueue=readOldArray(LEGACY_SOURCE_QUEUE_KEY);
 if(!oldDrafts.length&&!oldQueue.length)return {drafts:0,sources:0};
 const existing=await loadUnifiedSkillLibrary();
 const savedDraftIds=new Set(existing.drafts.map(x=>x.id));
 const savedSourceIds=new Set(existing.queue.map(x=>x.id));
 let drafts=0,sources=0;
 for(const legacy of oldDrafts){
  const id=string(legacy.id,110),name=string(legacy.name,80);
  if(!id||!name||forbidden(name)||savedDraftIds.has(id))continue;
  const draft:OwnerSkillDraft={
   id,name,description:string(legacy.description,240),
   category:string(legacy.category,90)||'General',
   instructions:string(legacy.instructions,5000),
   status:legacy.status==='needs review'?'needs review':'draft',
   assignedTo:arr(legacy.assignedTo),createdAt:string(legacy.createdAt,80)||new Date().toISOString(),
   inputs:arr(legacy.inputs),outputs:arr(legacy.outputs),
   tools:arr(legacy.tools),safetyRules:arr(legacy.safetyRules),
   presetRef:string(legacy.presetRef,220)||undefined
  };
  await saveOwnerSkillDraft(draft);savedDraftIds.add(id);drafts++;
 }
 for(const legacy of oldQueue){
  const id=string(legacy.id,110),name=string(legacy.name,90),
   url=string(legacy.url,1600);
  if(!id||!name||!url||forbidden(name)||forbidden(url)||savedSourceIds.has(id))continue;
  await saveOwnerSkillCandidate({id,name,url,notes:string(legacy.notes,400),
   status:'pending review'});
  savedSourceIds.add(id);sources++;
 }
 // Only after all eligible migration writes succeed; do not retain parallel stores.
 localStorage.removeItem(LEGACY_SKILL_DRAFT_KEY);
 localStorage.removeItem(LEGACY_SOURCE_QUEUE_KEY);
 return {drafts,sources};
}
